import React, { useState, useEffect, useCallback } from 'react';
import { Navbar, Footer, CourseCard } from '../../components';
import { formatCourseThumbnail } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import SEO from '../../components/common/SEO';

const CourseCardSkeleton = () => (
  <div className="lh-course-card card overflow-hidden animate-pulse">
    <div className="lh-course-card__thumbnail relative overflow-hidden bg-dark-200 aspect-[16/10]" />
    <div className="lh-course-card__content p-5">
      <div className="h-6 bg-dark-200 rounded mb-2" />
      <div className="h-4 bg-dark-200 rounded w-2/3 mb-3" />
      <div className="flex gap-4 mb-3">
        <div className="h-4 bg-dark-200 rounded w-16" />
        <div className="h-4 bg-dark-200 rounded w-20" />
      </div>
      <div className="h-4 bg-dark-200 rounded w-24 mb-4" />
      <div className="flex justify-between items-center">
        <div className="h-6 bg-dark-200 rounded w-20" />
        <div className="h-4 bg-dark-200 rounded w-24" />
      </div>
    </div>
  </div>
);

const Courses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0 });
  const [subscribedCourseIds, setSubscribedCourseIds] = useState(new Set());
  const [filters, setFilters] = useState({
    category: '',
    level: '',
    sort: 'newest',
  });

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user || user.role !== 'student') return;
      try {
        const data = await api.getMySubscription();
        if (data.isActive && data.subscription) {
          const ids = new Set();
          if (!data.subscription.allowedCourses || data.subscription.allowedCourses.length === 0) {
            ids.add('__all__');
          } else {
            data.subscription.allowedCourses.forEach(c => ids.add((c._id || c).toString()));
          }
          setSubscribedCourseIds(ids);
        }
      } catch {
        // not critical
      }
    };
    fetchSubscription();
  }, [user]);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage.toString(),
        limit: '12',
        sort: filters.sort,
      };
      if (searchQuery) params.search = searchQuery;
      if (filters.category) params.category = filters.category;
      if (filters.level) params.level = filters.level;

      const data = await api.getCourses(params);
      const apiCourses = (data.courses || []).map(course => {
        const mins = course.liveClassDurationMins || 0;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        let duration = '';
        if (h > 0 && m > 0) duration = `${h}h ${m}m`;
        else if (h > 0) duration = `${h}h`;
        else if (m > 0) duration = `${m}m`;

        return {
          id: course._id,
          _id: course._id,
          title: course.title,
          thumbnail: formatCourseThumbnail(course.thumbnail),
          duration: duration || null,
          liveClassCount: course.liveClassCount || 0,
          level: course.level,
          category: course.category,
        };
      });

      setCourses(apiCourses);
      setPagination(data.pagination || { page: 1, limit: 12, total: apiCourses.length });
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, currentPage]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  return (
    <div className="lh-courses-page min-h-screen bg-white">
      <SEO
        title="Browse Courses"
        description="Explore our catalog of expert-led online courses in React, Java, Python, SQL, DevOps, Machine Learning, and AI. Filter by category, level, and more."
        canonicalPath="/courses"
        keywords={['browse courses', 'programming courses', 'beginner courses', 'advanced courses', 'DevOps training', 'data science courses', 'software engineering']}
      />
      <Navbar />

      <section className="lh-courses__header pt-24 pb-12 bg-gradient-to-br from-dark-50 via-white to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-dark-900 mb-4">Explore All Courses</h1>
          <p className="text-lg text-dark-500">Discover courses to advance your skills and career</p>
        </div>
      </section>

      <section className="py-6 bg-white border-b border-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="w-full pl-12 pr-20 py-3 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                />
                <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm">
                  Search
                </button>
              </div>
            </form>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className="w-full px-3 sm:px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm col-span-2 sm:col-span-1">
                <option value="">All Categories</option>
                <option value="Web Development">Web Development</option>
                <option value="DevOps">DevOps</option>
                <option value="Programming">Programming</option>
                <option value="Data Science">Data Science</option>
                <option value="Mobile Development">Mobile Development</option>
              </select>

              <select value={filters.level} onChange={(e) => handleFilterChange('level', e.target.value)} className="w-full px-3 sm:px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm">
                <option value="">All Levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>

              <select value={filters.sort} onChange={(e) => handleFilterChange('sort', e.target.value)} className="w-full px-3 sm:px-4 py-3 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm">
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="popular">Popular</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <CourseCardSkeleton key={i} />)}
            </div>
          ) : courses.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course) => {
                  const courseId = (course.id || course._id || '').toString();
                  const isSubscribed = user?.role === 'student' && (subscribedCourseIds.has('__all__') || subscribedCourseIds.has(courseId));
                  return (
                    <CourseCard key={courseId} course={course} subscriptionStatus={user?.role === 'student' ? (isSubscribed ? 'subscribed' : 'not_subscribed') : null} />
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-4 py-2 border border-dark-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    Previous
                  </button>
                  <div className="flex gap-2">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button key={page} onClick={() => setCurrentPage(page)} className={`px-4 py-2 rounded-lg transition-colors ${currentPage === page ? 'bg-primary-500 text-white' : 'border border-dark-200 hover:bg-primary-50'}`}>
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-4 py-2 border border-dark-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    Next
                  </button>
                </div>
              )}

              <div className="text-center mt-4 text-dark-500 text-sm">
                Showing {((currentPage - 1) * pagination.limit) + 1} - {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} courses
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <svg className="w-24 h-24 mx-auto text-dark-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-dark-900 mb-2">No courses found</h3>
              <p className="text-dark-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Courses;
